import * as React from "react";
import { useRender } from "@base-ui/react/use-render";
import { cva } from "class-variance-authority";

import type { IconComponent } from "@ergon-ai/ui/icons";
import { cn } from "@ergon-ai/ui";
import { ICON_SIZE } from "@ergon-ai/ui/icons";
import { Spinner } from "@ergon-ai/ui/loader";

import { asChildElement } from "../internal/as-child";

export const ICON_BUTTON_STYLES = {
  variant: {
    surface:
      "bg-surface text-ink shadow-e1 hover:bg-surface-subtle active:bg-surface-subtle",
    tint: "bg-action-secondary text-action-secondary-ink hover:opacity-90 active:opacity-80",
    subtle: "bg-surface-low text-ink hover:opacity-90 active:opacity-80",
    ghost: "text-ink hover:bg-surface-subtle active:bg-surface-subtle/80",
    control: "bg-surface-control text-ink hover:opacity-90 active:opacity-80",
  },
  size: { sm: "size-[34px]", md: "size-[44px]" },
} as const;

const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: ICON_BUTTON_STYLES,
    defaultVariants: { variant: "surface", size: "md" },
  },
);

export interface IconButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  icon: IconComponent;
  /** Required: it is the accessible name. */
  label: string;
  variant?: keyof typeof ICON_BUTTON_STYLES.variant;
  size?: keyof typeof ICON_BUTTON_STYLES.size;
  shape?: "circle" | "rounded";
  loading?: boolean;
  indicator?: boolean;
  asChild?: boolean;
  children?: React.ReactElement;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon: Icon,
      label,
      variant,
      size = "md",
      shape = "circle",
      loading = false,
      indicator = false,
      asChild = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const glyph = size === "sm" ? ICON_SIZE.md : ICON_SIZE.lg;
    const child = asChildElement(asChild, children);
    let renderElement: React.ReactElement = <button />;
    if (child) {
      const { children: _inner, ...childProps } = child.props;
      renderElement = React.createElement(child.type, childProps);
    }
    return useRender({
      render: renderElement,
      ref,
      props: {
        "data-slot": "icon-button",
        "aria-label": label,
        "aria-busy": loading || undefined,
        disabled: disabled === true || loading,
        className: cn(
          iconButtonVariants({ variant, size }),
          shape === "rounded" && "rounded-md",
          indicator && "relative",
          (loading || props["aria-busy"] === true) && "disabled:opacity-100",
          className,
        ),
        ...props,
        children: (
          <>
            {loading ? (
              <Spinner size={glyph} />
            ) : (
              <Icon size={glyph} aria-hidden="true" />
            )}
            {indicator && !loading && (
              <span
                aria-hidden="true"
                className="absolute top-[9px] right-[9px] size-[8px] rounded-full bg-action-primary"
              />
            )}
          </>
        ),
      },
    });
  },
);
IconButton.displayName = "IconButton";

export { IconButton };
